<template>
    <div>
        <ContractPreview
            v-if="showPreview"
            ref="pdfViewer"
            :url="pdfUrl"
            v-on:close="showPreview = false"
        />

        <div v-if="state === 'loading'">
            <Loader></Loader>
        </div>
        <div v-else-if="state === 'new' || state === 'saving' || state === 'ready'" class="inner contract-wizard custom-wizard">

            <custom-contract-sidebar
                ref="customContractSidebar"
                :property="property"
                :custom-contract="customContract"
                :custom-contract-page="customContractPage"
                :state="state"
                v-on:preview="preview()"
                v-on:save="save()"
            />

            <div class="screening-upload contract-upload">
                <tabs
                    ref="customContractTabs"
                    :options="{useUrlFragment: false}"
                    v-on:changed="$refs.customContractSidebar.updateGreenMarks()">

                    <tab id="customContractUploadTab" :name="$t('digital.tabs.upload.tabname')" :is-disabled="state !== 'new'">
                        <custom-contract-upload
                            v-model="customContract"
                            :property="property"
                            v-on:success="showCustomContractPageSelectTab()"
                            v-on:error="error"
                        />
                    </tab>

                    <tab id="customContractPageSelectTab" :name="$t('digital.tabs.pages.tabname')" :is-disabled="state === 'new'">
                        <div class="edit-pdf-container">
                            <custom-contract-page-select
                                :custom-contract="customContract"
                                :state="state"
                                v-model="customContractPage"
                            />
                            <custom-contract-signature-editor
                                ref="customContractSignatureEditor"
                                :custom-contract="customContract"
                                :state="state"
                                v-model="customContractPage"
                                v-on:saving="state = 'saving'"
                                v-on:save="state = 'ready'"
                                v-on:error="error"
                            />
                        </div>
                    </tab>

                    <tab id="customContractLandlordTab" :name="$t('digital.tabs.landlord.tabname')" :is-disabled="state === 'new'">
                        <custom-contract-landlord
                            ref="customContractLandlord"
                            v-model="customContract"
                            :state="state"
                            v-on:saving="state = 'saving'"
                            v-on:save="state = 'ready'; $refs.customContractSidebar.goToNextTab()"
                            v-on:error="error"
                        />
                    </tab>

                    <tab id="customContractTenantTab" :name="$t('digital.tabs.tenant.tabname')" :is-disabled="state === 'new'">
                        <custom-contract-tenant
                            ref="customContractTenant"
                            v-model="customContract"
                            :state="state"
                            v-on:saving="state = 'saving'"
                            v-on:save="state = 'ready'; $refs.customContractSidebar.goToNextTab()"
                            v-on:error="error"
                        />
                    </tab>

                    <tab id="customContractReviewAndSendTab" :name="$t('digital.tabs.review.tabname')" :is-disabled="state === 'new'">
                        <custom-contract-review-and-send
                            ref="customContractReviewAndSend"
                            v-model="customContract"
                            :state="state"
                            v-on:preview="preview()"
                            v-on:sending="state = 'saving'"
                            v-on:send="$router.push({name: 'custom-contract::status', params: {id: customContract.id, canRevoke: true}})"
                            v-on:sign="$router.push({name: 'custom-contract::status', params: {id: customContract.id, canRevoke: true}})"
                            v-on:revoke="revoke"
                            v-on:error="error"
                        />
                    </tab>

                </tabs>
            </div>
        </div>
    </div>
</template>

<script>
import Loader from "../../../components/Loader";
import ContractPreview from '../../../components/ContractPreview.vue';

import CustomContractSidebar from "./CustomContractSidebar";
import CustomContractUpload from "./CustomContractUpload";
import CustomContractPageSelect from "./CustomContractPageSelect";
import CustomContractSignatureEditor from "./CustomContractSignatureEditor";
import CustomContractLandlord from "./CustomContractLandlord";
import CustomContractTenant from "./CustomContractTenant";

import {mapGetters} from "vuex";
import CustomContractReviewAndSend from "./CustomContractReviewAndSend";

import customContractMixin from '../../../mixins/custom-contract';

export default {
    name: "CustomContractWizzard",
    mixins: [customContractMixin],
    props: ['property-id'],
    components: {
        CustomContractReviewAndSend,
        Loader,
        ContractPreview,
        CustomContractSidebar,
        CustomContractUpload,
        CustomContractPageSelect,
        CustomContractSignatureEditor,
        CustomContractLandlord,
        CustomContractTenant,
    },
    data: function () {
        return {
            activeComponent: 'custom-contract-signature-editor',
            /**
             * States:
             *
             * new - the document was not uploaded, CustomContract entity was not created, only property is available
             * loading - CustomContract entity loading is in progress
             * ready - the existed CustomContract entity is ready for work
             * saving - background saving operation is in progress
             */
            state: 'new',
            resetState: null,
            showPreview: false,
            property: {},
            customContract: {},
            customContractPage: {}, // Selected page for Signature editor
        }
    },
    computed: {
        pdfUrl: function () {
            let customContractId = _.has(this.customContract, 'id') ? this.customContract.id : this.$route.params.id;

            return window.config.apiUrl + `api/custom-contract/${customContractId}.pdf?token=` + this.getToken;
        },
        ...mapGetters(['getToken'])
    },
    methods: {
        showCustomContractPageSelectTab() {
            this.state = 'ready';
            this.customContractPage = this.customContract.custom_contract_pages[0];
            this.$nextTick(() => {
                this.$refs.customContractTabs.selectTab('#customContractPageSelectTab');
            });
        },
        save() {
            switch(this.$refs.customContractTabs.activeTabHash) {
                case '#customContractPageSelectTab':
                    this.$refs.customContractSignatureEditor.save();
                    break;
                case '#customContractLandlordTab':
                    this.$refs.customContractLandlord.save();
                    break;
                case '#customContractTenantTab':
                    this.$refs.customContractTenant.save();
                    break;
            }
        },
        preview() {
            this.showPreview = true;
        },
    },
    created() {
        this.state = 'loading';

        // Edit action
        if (this.$route.params.id) {
            axios({
                method: 'GET',
                url: window.config.apiUrl + `api/custom-contract/${this.$route.params.id}`
            }).then((response) => {
                this.customContract = response.data.data;

                if (_.has(this.customContract.custom_contract_pages, {has_signatures: true})) {
                    this.customContractPage = _.find(this.customContract.custom_contract_pages, {has_signatures: true});
                } else {
                    this.customContractPage = _.first(this.customContract.custom_contract_pages);
                }

                this.property = this.customContract.property;

                if (this.customContract.is_completed) {
                    // Redirect to other view
                    this.$router.push({
                        name: 'custom-contract::status',
                        params: {
                            id: this.customContract.id,
                            canRevoke: false
                        }
                    });

                    return;
                }

                if ((this.customContract.is_landlord_approval_requested && !this.customContract.is_approved_by_landlord)
                    || this.customContract.is_sent_to_tenant && !this.customContract.is_signed_by_tenant) {
                    // Awaiting approval or signing
                    this.$router.push({
                        name: 'custom-contract::status',
                        params: {
                            id: this.customContract.id,
                            canRevoke: true
                        }
                    });

                    return;
                }

                this.resetState = this.state = 'ready';

                this.$nextTick(() => {
                    this.$refs.customContractTabs.selectTab('#customContractPageSelectTab');
                });
            })
        } else { // Create new action

            if(!_.isInteger(this.propertyId)) {
                this.$router.push({name: 'contracts'});
            }

            axios({
                method: 'GET',
                url: window.config.apiUrl + `api/property/${this.propertyId}`
            }).then((response) => {
                this.property = response.data;
                this.resetState = this.state = 'new';
            })
        }
    }
}
</script>

<style scoped>

</style>
